import { HardHat } from "lucide-react";
import type { TechnicianAttachment } from "@myapp/contract";
import { TechnicianAttachmentList } from "../TechnicianAttachmentList/TechnicianAttachmentList";

type Props = {
  comment: string | null | undefined;
  attachments: TechnicianAttachment[] | null | undefined;
  /** Why the technician asked for a Folgetermin — theirs like the comment, so it lives in this card. */
  followUpReason?: string | null;
  className?: string;
};

/**
 * The office's read-only view of what came back from the field: the technician's comment and
 * files, under one heading that names the source. Sits below the edit form in the upsert modals,
 * visibly apart from the form's own Beschreibung and Anhänge — those are the office's, these are
 * the worker's, and mixing them was the confusion this block exists to prevent.
 *
 * Renders nothing when the technician sent nothing — no reason, no comment, no files.
 */
export const TechnicianFeedbackSummary = ({
  comment,
  attachments,
  followUpReason,
  className,
}: Props) => {
  const text = comment?.trim() ?? "";
  const reason = followUpReason?.trim() ?? "";
  const files = attachments ?? [];
  if (!reason && !text && files.length === 0) return null;

  return (
    <section
      aria-label="Rückmeldung vom Handwerker"
      className={`rounded-[18px] border border-amber-200 bg-amber-50/60 p-4 ${className ?? ""}`}
    >
      <div className="flex items-center gap-2 text-amber-900">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100">
          <HardHat size={16} aria-hidden />
        </span>
        <h3 className="text-sm font-semibold">Rückmeldung vom Handwerker</h3>
      </div>

      {reason && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-amber-900/70">
            Grund für den Folgetermin
          </p>
          <p className="select-text whitespace-pre-wrap break-words text-sm text-slate-800">
            {reason}
          </p>
        </div>
      )}

      {text && (
        <p className="mt-3 select-text whitespace-pre-wrap break-words text-sm text-slate-800">
          {text}
        </p>
      )}

      {files.length > 0 && (
        <div className="mt-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-900/70">
            Dateien vom Handwerker ({files.length})
          </p>
          <TechnicianAttachmentList items={files} />
        </div>
      )}
    </section>
  );
};
